// lib/locationsAdmin.ts
import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
  type Query,
} from "firebase/firestore";
import { normalizeLabel } from "@/lib/taxonomy";

export type ModerationStatus = "approved" | "pending" | "rejected";

const COUNTIES = "counties";
const CITIES = "cities";
const AREAS = "areas";
const CITY_SUGGESTIONS = "citySuggestions";
const AREA_SUGGESTIONS = "areaSuggestions";

/** ---------------- types ---------------- */

export type AdminCounty = {
  id: string;
  name: string;
  nameNormalized: string;
  sortOrder?: number;
  isDeleted?: boolean;
  status?: ModerationStatus;
  createdAt?: any;
  updatedAt?: any;
};

export type AdminCity = {
  id: string;
  name: string;
  nameNormalized: string;
  sortOrder: number;
  isDeleted: boolean;
  status: ModerationStatus;
  countyId?: string;
  countyName?: string;
  createdAt?: any;
  updatedAt?: any;
  _path?: string;
};

export type AdminArea = {
  id: string;
  name: string;
  nameNormalized: string;
  sortOrder: number;
  isDeleted: boolean;
  status: ModerationStatus;
  countyId?: string;
  countyName?: string;
  cityId?: string;
  cityName?: string;
  createdAt?: any;
  updatedAt?: any;
  _path?: string;
};

export type AdminCitySuggestion = {
  id: string;
  type: "city";
  status: ModerationStatus;
  countyId: string;
  countyName?: string;
  label: string;
  labelLower: string;
  createdByUid?: string;
  createdAt?: any;
  updatedAt?: any;
  _path?: string;
};

export type AdminAreaSuggestion = {
  id: string;
  type: "area";
  status: ModerationStatus;
  countyId: string;
  countyName?: string;
  cityId?: string | null;
  cityName?: string | null;
  citySuggestionId?: string | null;
  citySuggestionName?: string | null;
  label: string;
  labelLower: string;
  createdByUid?: string;
  createdAt?: any;
  updatedAt?: any;
  _path?: string;
};

/** ---------------- helpers ---------------- */

function asBool(v: any, fallback = false) {
  return typeof v === "boolean" ? v : fallback;
}

function asNum(v: any, fallback = 0) {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function asStr(v: any, fallback = "") {
  return typeof v === "string" ? v : fallback;
}

function pickNormalized(d: any) {
  return typeof d?.nameNormalized === "string"
    ? d.nameNormalized
    : typeof d?.normalized === "string"
    ? d.normalized
    : normalizeLabel(d?.name ?? "");
}

function assertId(label: string, id: string) {
  if (!id) throw new Error(`${label} is required.`);
}

function dumpError(label: string, e: any) {
  const code = String(e?.code ?? "").trim().slice(0, 80);
  console.error(`${label} Firestore request failed.`, code ? { code } : undefined);

  try {
    // @ts-ignore
    globalThis.__LAST_ADMIN_LOC_ERROR__ = code || "unknown";
  } catch {}
}

async function getDocsWithIndexDebug<
  AppModelType = DocumentData,
  DbModelType extends DocumentData = DocumentData
>(label: string, q: Query, _meta?: Record<string, any>) {
  console.log(`${label} Firestore request started.`);
  try {
    return await getDocs<AppModelType, DbModelType>(q as any);
  } catch (e: any) {
    dumpError(label, e);
    throw e;
  }
}

/** ---------------- LISTING ---------------- */

export async function adminListCounties(db: Firestore): Promise<AdminCounty[]> {
  const col = collection(db, COUNTIES);
  const q = query(col, orderBy("name", "asc"), limit(200));
  const snap = await getDocsWithIndexDebug<DocumentData>("ADMIN COUNTIES", q);

  return snap.docs.map((d) => {
    const data = d.data() as DocumentData;
    return {
      id: d.id,
      name: asStr(data.name),
      nameNormalized: pickNormalized(data),
      sortOrder: asNum(data.sortOrder, 0),
      isDeleted: asBool(data.isDeleted, false),
      status: (data.status as ModerationStatus) ?? "approved",
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      ...data,
    } as AdminCounty;
  });
}

/** -------- Cities (nested) -------- */

export async function adminListCities(
  db: Firestore,
  args: { countyId: string; status?: ModerationStatus; includeDeleted?: boolean }
): Promise<AdminCity[]> {
  const { countyId, status, includeDeleted } = args;
  assertId("adminListCities: countyId", countyId);

  const col = collection(db, COUNTIES, countyId, CITIES);
  const clauses: any[] = [];

  if (status) clauses.push(where("status", "==", status));
  if (!includeDeleted) clauses.push(where("isDeleted", "==", false));

  const q = query(
    col,
    ...clauses,
    orderBy("sortOrder", "asc"),
    orderBy("name", "asc"),
    limit(1000)
  );

  const snap = await getDocsWithIndexDebug<DocumentData>("ADMIN CITIES", q, {
    scope: "nested",
    countyId,
    status: status ?? null,
    includeDeleted: !!includeDeleted,
  });

  return snap.docs.map((d) => {
    const data = d.data() as DocumentData;
    return {
      id: d.id,
      name: asStr(data.name),
      nameNormalized: pickNormalized(data),
      sortOrder: asNum(data.sortOrder, 0),
      isDeleted: asBool(data.isDeleted, false),
      status: (data.status as ModerationStatus) ?? "pending",
      countyId: asStr(data.countyId, countyId),
      countyName: asStr(data.countyName, ""),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      ...data,
    } as AdminCity;
  });
}

/** -------- Areas (nested) -------- */

export async function adminListAreas(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    status?: ModerationStatus;
    includeDeleted?: boolean;
  }
): Promise<AdminArea[]> {
  const { countyId, cityId, status, includeDeleted } = args;
  assertId("adminListAreas: countyId", countyId);
  assertId("adminListAreas: cityId", cityId);

  const col = collection(db, COUNTIES, countyId, CITIES, cityId, AREAS);
  const clauses: any[] = [];

  if (status) clauses.push(where("status", "==", status));
  if (!includeDeleted) clauses.push(where("isDeleted", "==", false));

  const q = query(
    col,
    ...clauses,
    orderBy("sortOrder", "asc"),
    orderBy("name", "asc"),
    limit(2000)
  );

  const snap = await getDocsWithIndexDebug<DocumentData>("ADMIN AREAS", q, {
    scope: "nested",
    countyId,
    cityId,
    status: status ?? null,
    includeDeleted: !!includeDeleted,
  });

  return snap.docs.map((d) => {
    const data = d.data() as DocumentData;
    return {
      id: d.id,
      name: asStr(data.name),
      nameNormalized: pickNormalized(data),
      sortOrder: asNum(data.sortOrder, 0),
      isDeleted: asBool(data.isDeleted, false),
      status: (data.status as ModerationStatus) ?? "pending",
      countyId: asStr(data.countyId, countyId),
      countyName: asStr(data.countyName, ""),
      cityId: asStr(data.cityId, cityId),
      cityName: asStr(data.cityName, ""),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      ...data,
    } as AdminArea;
  });
}

/** -------- Global Cities (collectionGroup) -------- */

export async function adminListCitiesGlobal(
  db: Firestore,
  args: {
    status?: ModerationStatus;
    includeDeleted?: boolean;
    limitCount?: number;
    orderByField?: "name" | "sortOrder";
  } = {}
): Promise<AdminCity[]> {
  const { status, includeDeleted, limitCount, orderByField } = args;
  const col = collectionGroup(db, CITIES);
  const clauses: any[] = [];

  if (status) clauses.push(where("status", "==", status));
  if (!includeDeleted) clauses.push(where("isDeleted", "==", false));

  const q =
    orderByField === "sortOrder"
      ? query(
          col,
          ...clauses,
          orderBy("sortOrder", "asc"),
          orderBy("name", "asc"),
          limit(limitCount ?? 2000)
        )
      : query(col, ...clauses, orderBy("name", "asc"), limit(limitCount ?? 2000));

  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN CITIES GLOBAL",
    q,
    {
      scope: "collectionGroup",
      status: status ?? null,
      includeDeleted: !!includeDeleted,
    }
  );

  return snap.docs.map((d) => {
    const data = d.data() as DocumentData;
    return {
      id: d.id,
      name: asStr(data.name),
      nameNormalized: pickNormalized(data),
      sortOrder: asNum(data.sortOrder, 0),
      isDeleted: asBool(data.isDeleted, false),
      status: (data.status as ModerationStatus) ?? "pending",
      countyId: asStr(data.countyId, ""),
      countyName: asStr(data.countyName, ""),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      _path: d.ref.path,
      ...data,
    } as AdminCity;
  });
}

/** -------- Global Areas (collectionGroup) -------- */

export async function adminListAreasGlobal(
  db: Firestore,
  args: {
    status?: ModerationStatus;
    includeDeleted?: boolean;
    limitCount?: number;
    orderByField?: "name" | "sortOrder";
  } = {}
): Promise<AdminArea[]> {
  const { status, includeDeleted, limitCount, orderByField } = args;
  const col = collectionGroup(db, AREAS);
  const clauses: any[] = [];

  if (status) clauses.push(where("status", "==", status));
  if (!includeDeleted) clauses.push(where("isDeleted", "==", false));

  const q =
    orderByField === "sortOrder"
      ? query(
          col,
          ...clauses,
          orderBy("sortOrder", "asc"),
          orderBy("name", "asc"),
          limit(limitCount ?? 3000)
        )
      : query(col, ...clauses, orderBy("name", "asc"), limit(limitCount ?? 3000));

  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN AREAS GLOBAL",
    q,
    {
      scope: "collectionGroup",
      status: status ?? null,
      includeDeleted: !!includeDeleted,
    }
  );

  return snap.docs.map((d) => {
    const data = d.data() as DocumentData;
    return {
      id: d.id,
      name: asStr(data.name),
      nameNormalized: pickNormalized(data),
      sortOrder: asNum(data.sortOrder, 0),
      isDeleted: asBool(data.isDeleted, false),
      status: (data.status as ModerationStatus) ?? "pending",
      countyId: asStr(data.countyId, ""),
      countyName: asStr(data.countyName, ""),
      cityId: asStr(data.cityId, ""),
      cityName: asStr(data.cityName, ""),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      _path: d.ref.path,
      ...data,
    } as AdminArea;
  });
}

/** -------- Global City Suggestions (collectionGroup) -------- */

export async function adminListCitySuggestionsGlobal(
  db: Firestore,
  args: { status?: ModerationStatus; limitCount?: number } = {}
): Promise<AdminCitySuggestion[]> {
  const col = collectionGroup(db, CITY_SUGGESTIONS);
  const clauses: any[] = [];

  if (args.status) clauses.push(where("status", "==", args.status));

  const q = query(
    col,
    ...clauses,
    orderBy("createdAt", "desc"),
    limit(args.limitCount ?? 500)
  );

  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN CITY SUGGESTIONS GLOBAL",
    q,
    { scope: "collectionGroup", status: args.status ?? null }
  );

  return snap.docs.map((d) => {
    const data = d.data() as DocumentData;
    return {
      id: d.id,
      type: "city",
      status: (data.status as ModerationStatus) ?? "pending",
      countyId: asStr(data.countyId, ""),
      countyName: asStr(data.countyName, ""),
      label: asStr(data.label),
      labelLower: asStr(data.labelLower),
      createdByUid: asStr(data.createdByUid, ""),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      _path: d.ref.path,
      ...data,
    } as AdminCitySuggestion;
  });
}

/** -------- Global Area Suggestions (collectionGroup) -------- */

export async function adminListAreaSuggestionsGlobal(
  db: Firestore,
  args: { status?: ModerationStatus; limitCount?: number } = {}
): Promise<AdminAreaSuggestion[]> {
  const col = collectionGroup(db, AREA_SUGGESTIONS);
  const clauses: any[] = [];

  if (args.status) clauses.push(where("status", "==", args.status));

  const q = query(
    col,
    ...clauses,
    orderBy("createdAt", "desc"),
    limit(args.limitCount ?? 1000)
  );

  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN AREA SUGGESTIONS GLOBAL",
    q,
    { scope: "collectionGroup", status: args.status ?? null }
  );

  return snap.docs.map((d) => {
    const data = d.data() as DocumentData;
    return {
      id: d.id,
      type: "area",
      status: (data.status as ModerationStatus) ?? "pending",
      countyId: asStr(data.countyId, ""),
      countyName: asStr(data.countyName, ""),
      cityId: (data.cityId ?? null) as string | null,
      cityName: (data.cityName ?? null) as string | null,
      citySuggestionId: (data.citySuggestionId ?? null) as string | null,
      citySuggestionName: (data.citySuggestionName ?? null) as string | null,
      label: asStr(data.label),
      labelLower: asStr(data.labelLower),
      createdByUid: asStr(data.createdByUid, ""),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      _path: d.ref.path,
      ...data,
    } as AdminAreaSuggestion;
  });
}

/** -------- Pending queue -------- */

export async function adminListPendingLocationSuggestions(
  db: Firestore,
  args: { limitCount?: number } = {}
): Promise<(AdminCitySuggestion | AdminAreaSuggestion)[]> {
  const [cities, areas] = await Promise.all([
    adminListCitySuggestionsGlobal(db, {
      status: "pending",
      limitCount: args.limitCount,
    }),
    adminListAreaSuggestionsGlobal(db, {
      status: "pending",
      limitCount: args.limitCount,
    }),
  ]);

  return [...cities, ...areas].sort((a: any, b: any) => {
    const at = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds ?? 0;
    const bt = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds ?? 0;
    return bt - at;
  });
}

/** ---------------- MUTATIONS (ADMIN) ---------------- */

export async function adminCreateCity(
  db: Firestore,
  args: {
    countyId: string;
    name: string;
    sortOrder?: number;
    status?: ModerationStatus;
    countyName?: string;
  }
) {
  const { countyId, name, sortOrder, status, countyName } = args;
  assertId("adminCreateCity: countyId", countyId);

  if (!name?.trim()) {
    throw new Error("adminCreateCity: name is required.");
  }

  const col = collection(db, COUNTIES, countyId, CITIES);
  const normalized = normalizeLabel(name);
  const payload = {
    name: name.trim(),
    nameNormalized: normalized,
    normalized,
    sortOrder: typeof sortOrder === "number" ? sortOrder : 0,
    status: status ?? "approved",
    isDeleted: false,
    countyId,
    countyName: countyName ?? "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(col, payload);
  return { id: ref.id, ...payload };
}

export async function adminCreateArea(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    name: string;
    sortOrder?: number;
    status?: ModerationStatus;
    countyName?: string;
    cityName?: string;
  }
) {
  const { countyId, cityId, name, sortOrder, status, countyName, cityName } =
    args;

  assertId("adminCreateArea: countyId", countyId);
  assertId("adminCreateArea: cityId", cityId);

  if (!name?.trim()) {
    throw new Error("adminCreateArea: name is required.");
  }

  const col = collection(db, COUNTIES, countyId, CITIES, cityId, AREAS);
  const normalized = normalizeLabel(name);
  const payload = {
    name: name.trim(),
    nameNormalized: normalized,
    normalized,
    sortOrder: typeof sortOrder === "number" ? sortOrder : 0,
    status: status ?? "approved",
    isDeleted: false,
    countyId,
    countyName: countyName ?? "",
    cityId,
    cityName: cityName ?? "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(col, payload);
  return { id: ref.id, ...payload };
}

export async function adminUpdateCity(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    patch: Partial<{
      name: string;
      sortOrder: number;
      status: ModerationStatus;
      isDeleted: boolean;
      countyName: string;
    }>;
  }
) {
  const { countyId, cityId, patch } = args;
  assertId("adminUpdateCity: countyId", countyId);
  assertId("adminUpdateCity: cityId", cityId);

  const ref = doc(db, COUNTIES, countyId, CITIES, cityId);
  const update: Record<string, any> = {
    ...patch,
    updatedAt: serverTimestamp(),
  };

  if (typeof patch?.name === "string") {
    update.name = patch.name.trim();
    update.nameNormalized = normalizeLabel(patch.name);
    update.normalized = normalizeLabel(patch.name);
  }

  await updateDoc(ref, update);
  return true;
}

export async function adminUpdateArea(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    areaId: string;
    patch: Partial<{
      name: string;
      sortOrder: number;
      status: ModerationStatus;
      isDeleted: boolean;
      countyName: string;
      cityName: string;
    }>;
  }
) {
  const { countyId, cityId, areaId, patch } = args;
  assertId("adminUpdateArea: countyId", countyId);
  assertId("adminUpdateArea: cityId", cityId);
  assertId("adminUpdateArea: areaId", areaId);

  const ref = doc(db, COUNTIES, countyId, CITIES, cityId, AREAS, areaId);
  const update: Record<string, any> = {
    ...patch,
    updatedAt: serverTimestamp(),
  };

  if (typeof patch?.name === "string") {
    update.name = patch.name.trim();
    update.nameNormalized = normalizeLabel(patch.name);
    update.normalized = normalizeLabel(patch.name);
  }

  await updateDoc(ref, update);
  return true;
}

export async function adminSetCityStatus(
  db: Firestore,
  args: { countyId: string; cityId: string; status: ModerationStatus }
) {
  return adminUpdateCity(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    patch: { status: args.status },
  });
}

export async function adminSetAreaStatus(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    areaId: string;
    status: ModerationStatus;
  }
) {
  return adminUpdateArea(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    areaId: args.areaId,
    patch: { status: args.status },
  });
}

export async function adminSetCityDeleted(
  db: Firestore,
  args: { countyId: string; cityId: string; isDeleted: boolean }
) {
  return adminUpdateCity(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    patch: { isDeleted: args.isDeleted },
  });
}

export async function adminSetAreaDeleted(
  db: Firestore,
  args: { countyId: string; cityId: string; areaId: string; isDeleted: boolean }
) {
  return adminUpdateArea(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    areaId: args.areaId,
    patch: { isDeleted: args.isDeleted },
  });
}

export async function adminSetCitySortOrder(
  db: Firestore,
  args: { countyId: string; cityId: string; sortOrder: number }
) {
  return adminUpdateCity(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    patch: { sortOrder: args.sortOrder },
  });
}

export async function adminSetAreaSortOrder(
  db: Firestore,
  args: { countyId: string; cityId: string; areaId: string; sortOrder: number }
) {
  return adminUpdateArea(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    areaId: args.areaId,
    patch: { sortOrder: args.sortOrder },
  });
}

export async function adminRenameCity(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    name: string;
    adminUid?: string;
  }
) {
  return adminUpdateCity(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    patch: { name: args.name },
  });
}

export async function adminRenameArea(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    areaId: string;
    name: string;
    adminUid?: string;
  }
) {
  return adminUpdateArea(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    areaId: args.areaId,
    patch: { name: args.name },
  });
}

/** ---------------- QUICK LOOKUPS ---------------- */

export async function adminFindCitiesByNameNormalized(
  db: Firestore,
  args: { name: string; status?: ModerationStatus; includeDeleted?: boolean }
) {
  const nameNormalized = normalizeLabel(args.name);
  const col = collectionGroup(db, CITIES);
  const clauses: any[] = [where("nameNormalized", "==", nameNormalized)];

  if (args.status) clauses.push(where("status", "==", args.status));
  if (!args.includeDeleted) clauses.push(where("isDeleted", "==", false));

  const q = query(col, ...clauses, limit(50));
  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN FIND CITIES (nameNormalized)",
    q,
    {
      scope: "collectionGroup",
      nameNormalized,
      status: args.status ?? null,
    }
  );

  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }));
}

export async function adminFindAreasByNameNormalized(
  db: Firestore,
  args: { name: string; status?: ModerationStatus; includeDeleted?: boolean }
) {
  const nameNormalized = normalizeLabel(args.name);
  const col = collectionGroup(db, AREAS);
  const clauses: any[] = [where("nameNormalized", "==", nameNormalized)];

  if (args.status) clauses.push(where("status", "==", args.status));
  if (!args.includeDeleted) clauses.push(where("isDeleted", "==", false));

  const q = query(col, ...clauses, limit(50));
  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN FIND AREAS (nameNormalized)",
    q,
    {
      scope: "collectionGroup",
      nameNormalized,
      status: args.status ?? null,
    }
  );

  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }));
}

/** ---------------- NAME EXISTS HELPERS ---------------- */

export async function adminCityNameExists(
  db: Firestore,
  countyId: string,
  name: string
): Promise<boolean> {
  assertId("adminCityNameExists: countyId", countyId);
  if (!name?.trim()) return false;

  const nameNormalized = normalizeLabel(name);
  const col = collection(db, COUNTIES, countyId, CITIES);
  const q = query(
    col,
    where("nameNormalized", "==", nameNormalized),
    where("isDeleted", "==", false),
    limit(1)
  );

  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN CITY NAME EXISTS",
    q,
    { countyId, nameNormalized }
  );

  return !snap.empty;
}

export async function adminAreaNameExists(
  db: Firestore,
  countyId: string,
  cityId: string,
  name: string
): Promise<boolean> {
  assertId("adminAreaNameExists: countyId", countyId);
  assertId("adminAreaNameExists: cityId", cityId);
  if (!name?.trim()) return false;

  const nameNormalized = normalizeLabel(name);
  const col = collection(db, COUNTIES, countyId, CITIES, cityId, AREAS);
  const q = query(
    col,
    where("nameNormalized", "==", nameNormalized),
    where("isDeleted", "==", false),
    limit(1)
  );

  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN AREA NAME EXISTS",
    q,
    { countyId, cityId, nameNormalized }
  );

  return !snap.empty;
}

async function adminFindApprovedCityInCountyByNameNormalized(
  db: Firestore,
  args: { countyId: string; name: string }
) {
  assertId("adminFindApprovedCityInCountyByNameNormalized: countyId", args.countyId);

  const nameNormalized = normalizeLabel(args.name);
  const col = collection(db, COUNTIES, args.countyId, CITIES);
  const q = query(
    col,
    where("nameNormalized", "==", nameNormalized),
    where("status", "==", "approved"),
    where("isDeleted", "==", false),
    limit(1)
  );

  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN FIND APPROVED CITY IN COUNTY",
    q,
    { countyId: args.countyId, nameNormalized }
  );

  if (snap.empty) return null;

  const first = snap.docs[0];
  const data = first.data() as DocumentData;

  return {
    id: first.id,
    name: asStr(data.name, args.name.trim()),
  };
}

async function adminFindApprovedAreaInCityByNameNormalized(
  db: Firestore,
  args: { countyId: string; cityId: string; name: string }
) {
  assertId("adminFindApprovedAreaInCityByNameNormalized: countyId", args.countyId);
  assertId("adminFindApprovedAreaInCityByNameNormalized: cityId", args.cityId);

  const nameNormalized = normalizeLabel(args.name);
  const col = collection(db, COUNTIES, args.countyId, CITIES, args.cityId, AREAS);
  const q = query(
    col,
    where("nameNormalized", "==", nameNormalized),
    where("status", "==", "approved"),
    where("isDeleted", "==", false),
    limit(1)
  );

  const snap = await getDocsWithIndexDebug<DocumentData>(
    "ADMIN FIND APPROVED AREA IN CITY",
    q,
    { countyId: args.countyId, cityId: args.cityId, nameNormalized }
  );

  if (snap.empty) return null;

  const first = snap.docs[0];
  const data = first.data() as DocumentData;

  return {
    id: first.id,
    name: asStr(data.name, args.name.trim()),
  };
}

/** ---------------- COMPATIBILITY WRAPPERS ---------------- */

export async function adminCreateApprovedCity(
  db: Firestore,
  args: {
    countyId: string;
    name: string;
    sortOrder?: number;
    countyName?: string;
    adminUid?: string;
  }
) {
  return adminCreateCity(db, {
    countyId: args.countyId,
    name: args.name,
    sortOrder: args.sortOrder,
    countyName: args.countyName,
    status: "approved",
  });
}

export async function adminCreateApprovedArea(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    name: string;
    sortOrder?: number;
    countyName?: string;
    cityName?: string;
    adminUid?: string;
  }
) {
  return adminCreateArea(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    name: args.name,
    sortOrder: args.sortOrder,
    countyName: args.countyName,
    cityName: args.cityName,
    status: "approved",
  });
}

export async function adminApproveCity(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    adminUid?: string;
  }
) {
  return adminSetCityStatus(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    status: "approved",
  });
}

export async function adminRejectCity(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    adminUid?: string;
  }
) {
  return adminSetCityStatus(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    status: "rejected",
  });
}

export async function adminApproveArea(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    areaId: string;
    adminUid?: string;
  }
) {
  return adminSetAreaStatus(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    areaId: args.areaId,
    status: "approved",
  });
}

export async function adminRejectArea(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    areaId: string;
    adminUid?: string;
  }
) {
  return adminSetAreaStatus(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    areaId: args.areaId,
    status: "rejected",
  });
}

export async function adminSoftDeleteCity(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    adminUid?: string;
  }
) {
  return adminSetCityDeleted(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    isDeleted: true,
  });
}

export async function adminRestoreCity(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    adminUid?: string;
  }
) {
  return adminSetCityDeleted(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    isDeleted: false,
  });
}

export async function adminSoftDeleteArea(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    areaId: string;
    adminUid?: string;
  }
) {
  return adminSetAreaDeleted(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    areaId: args.areaId,
    isDeleted: true,
  });
}

export async function adminRestoreArea(
  db: Firestore,
  args: {
    countyId: string;
    cityId: string;
    areaId: string;
    adminUid?: string;
  }
) {
  return adminSetAreaDeleted(db, {
    countyId: args.countyId,
    cityId: args.cityId,
    areaId: args.areaId,
    isDeleted: false,
  });
}

/** ---------------- SUGGESTIONS MODERATION ---------------- */

export async function adminRejectCitySuggestion(
  db: Firestore,
  args: { suggestionPath: string }
) {
  if (!args.suggestionPath) throw new Error("Missing suggestionPath");

  await updateDoc(doc(db, args.suggestionPath), {
    status: "rejected",
    updatedAt: serverTimestamp(),
  });

  return true;
}

export async function adminApproveCitySuggestion(
  db: Firestore,
  args: {
    suggestionPath: string;
    countyId: string;
    countyName?: string;
    label: string;
  }
) {
  assertId("adminApproveCitySuggestion: countyId", args.countyId);

  if (!args.suggestionPath) throw new Error("Missing suggestionPath");
  if (!args.label?.trim()) throw new Error("Missing label");

  const existing = await adminFindApprovedCityInCountyByNameNormalized(db, {
    countyId: args.countyId,
    name: args.label.trim(),
  });

  const approvedCity =
    existing ??
    (await adminCreateCity(db, {
      countyId: args.countyId,
      countyName: args.countyName ?? "",
      name: args.label.trim(),
      status: "approved",
    }));

  await updateDoc(doc(db, args.suggestionPath), {
    status: "approved",
    approvedCityId: approvedCity.id,
    approvedCityName: approvedCity.name,
    updatedAt: serverTimestamp(),
  });

  return { cityId: approvedCity.id };
}

export async function adminRejectAreaSuggestion(
  db: Firestore,
  args: { suggestionPath: string }
) {
  if (!args.suggestionPath) throw new Error("Missing suggestionPath");

  await updateDoc(doc(db, args.suggestionPath), {
    status: "rejected",
    updatedAt: serverTimestamp(),
  });

  return true;
}

export async function adminApproveAreaSuggestion(
  db: Firestore,
  args: {
    suggestionPath: string;
    countyId: string;
    countyName?: string;
    label: string;
    cityId?: string | null;
    cityName?: string | null;
    citySuggestionId?: string | null;
    citySuggestionName?: string | null;
  }
) {
  assertId("adminApproveAreaSuggestion: countyId", args.countyId);

  if (!args.suggestionPath) throw new Error("Missing suggestionPath");
  if (!args.label?.trim()) throw new Error("Missing label");

  let cityId = args.cityId ?? null;
  let cityName = args.cityName ?? null;

  if (!cityId && args.citySuggestionId) {
    const citySugPath = `${COUNTIES}/${args.countyId}/${CITY_SUGGESTIONS}/${args.citySuggestionId}`;
    const snap = await getDoc(doc(db, citySugPath));

    const approvedCityId = snap.exists()
      ? (snap.data() as any)?.approvedCityId
      : null;

    const approvedCityName = snap.exists()
      ? (snap.data() as any)?.approvedCityName
      : null;

    if (!approvedCityId) {
      throw new Error(
        "This area belongs to a pending city suggestion. Approve the city suggestion first."
      );
    }

    cityId = String(approvedCityId);
    cityName = approvedCityName
      ? String(approvedCityName)
      : args.citySuggestionName ?? null;
  }

  if (!cityId) {
    throw new Error("Missing cityId for approving area suggestion.");
  }

  const existing = await adminFindApprovedAreaInCityByNameNormalized(db, {
    countyId: args.countyId,
    cityId,
    name: args.label.trim(),
  });

  const approvedArea =
    existing ??
    (await adminCreateArea(db, {
      countyId: args.countyId,
      countyName: args.countyName ?? "",
      cityId,
      cityName: cityName ?? "",
      name: args.label.trim(),
      status: "approved",
    }));

  await updateDoc(doc(db, args.suggestionPath), {
    status: "approved",
    approvedCityId: cityId,
    approvedAreaId: approvedArea.id,
    approvedAreaName: approvedArea.name,
    updatedAt: serverTimestamp(),
  });

  return { areaId: approvedArea.id };
}
